import { MessageSourceProps, withMessages } from 'react-message-source';
import React, { Component } from 'react';
import { ResourceGroupDto, ResourceGroupSearchDto, UpsertResourceGroupDto } from 'ts-types/api.types';
import axios, { CancelTokenSource } from 'axios';
import { deleteById, getAllResourceGroups, getResourceGroupById } from 'services/resourceGroupServices';
import { noop } from 'utils/functionUtils';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import PxGrid from 'components/layout/PxGrid';
import { Grid, Icon, Message, Modal } from 'semantic-ui-react';
import CheckBox from 'components/final-form/CheckBox';
import AddButton from 'components/AddButton';
import SearchResultComponent from 'components/SearchResultComponent';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import EditActionButton from 'components/EditActionButton';
import DeleteActionButton from 'components/DeleteActionButton';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import UpsertResourceGroupForm from 'components/settings/resource-groups/UpsertResourceGroupForm';
import { errorUtils } from 'utils/errorUtils';
import { debounce } from 'ts-debounce';
import { FormState } from 'final-form';
import LoaderComponent from 'components/LoaderComponent';

const initialResourceGroupSearch: Partial<ResourceGroupSearchDto> = {
  includesInactive: false
};

interface ResourceGroupProps extends FormState {
  values: Partial<ResourceGroupSearchDto>
}

interface Props extends MessageSourceProps {

}

interface State {
  foundGroups: Array<ResourceGroupDto>,
  groupsLoaded: boolean,
  resourceGroup?: UpsertResourceGroupDto,
  resourceGroupId?: number,
  addOrEditModalOpen: boolean,
  cancelTokenSource: CancelTokenSource,
  errorMessage?: string,
  searchResourceGroups: Partial<ResourceGroupSearchDto>,

}

class ResourceGroupsView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);


    const searchResourceGroups = initialResourceGroupSearch;
    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundGroups: [],
      groupsLoaded: false,
      resourceGroup: undefined,
      resourceGroupId: undefined,
      addOrEditModalOpen: false,
      searchResourceGroups,
      cancelTokenSource

    }

    this.fetchResourceGroupsData(searchResourceGroups, cancelTokenSource);
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state
    cancelTokenSource && cancelTokenSource.cancel("Operation canceled by user");
  }

  fetchResourceGroupsData = async (
      searchResourceGroups: Partial<ResourceGroupSearchDto>,
      cancelTokenSource: CancelTokenSource) => {

    const onFinally = () => {
      this.setState({
        groupsLoaded: true
      })
    };

    getAllResourceGroups(searchResourceGroups, cancelTokenSource)
        .then(response =>
            this.setState({
              foundGroups: response
            })
        )
        .catch((error) => this.handleError(error.response.data))
        .finally(onFinally);
    this.hideModal();
  };

  openAddOrEditModal = (id: number) => {
    const {cancelTokenSource} = this.state;

    if (id) {
      getResourceGroupById(id, cancelTokenSource).then(response => {
        this.showModalResource(id, response)
      }).catch(noop)
    }
  };

  showModalResource = (id?: number, resourceGroup?: UpsertResourceGroupDto) => {
    this.setState(({}: State) => {
      return {
        addOrEditModalOpen: true,
        resourceGroup: resourceGroup,
        resourceGroupId: id
      }
    });

  };

  hideModal = () => {
    this.setState(({}: State) => {
      return {
        addOrEditModalOpen: false,
        resourceGroup: undefined,
        resourceGroupId: undefined
      }
    });
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  handleError = (error: any) => {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors: Array<string> = [errorUtils.deletingResourceGroupsNotAllowed];

    const violations: Array<any> = error.violations;
    let knowErrorFound = false;
    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          knowErrorFound = true;
          this.setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    }

    if (!knowErrorFound) {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        this.setErrorMessage(getMessage('error.general'));
      }
    }
  };


  deleteResourceGroup = (id: number): void => {
    const {cancelTokenSource} = this.state;

    deleteById(id, cancelTokenSource).then(response => {
      this.setState(({foundGroups}: State) => ({
        foundGroups: foundGroups.filter((resourceGroup: ResourceGroupDto) => resourceGroup.id != response.id)
      }))
    }).catch((error) => this.handleError(error.response.data))
        .finally(this.onResourceGroupsChange)
  };

  onResourceGroupsChange = () => {
    const {searchResourceGroups, cancelTokenSource} = this.state;

    const onFinally = () => {
      this.setState({
        groupsLoaded: true
      })
    };

    getAllResourceGroups(searchResourceGroups, cancelTokenSource)
        .then(response => {
          this.setState({
            foundGroups: response
          });
        })
        .catch(noop)
        .finally(onFinally);
    this.hideModal();
  }


  render(): React.ReactNode {
    const {getMessage} = this.props
    const {groupsLoaded, errorMessage} = this.state

    return (
        <PraxStyledSegment>
          {errorMessage && this.renderErrorSegment()}
          <div>
            {this.addResourceGroupsSegment()}
          </div>

          <StyledViewTableRow>
            {!groupsLoaded
                ? <LoaderComponent message={getMessage("resourceGroups.loading")}/>
                : this.renderResourceGroupsSegment()
            }
          </StyledViewTableRow>

          {this.renderUpsertResourceGroupsModal()}

        </PraxStyledSegment>
    )
  }

  handleChange = debounce(({values}: ResourceGroupProps): void => {
    if (!this.mounted) {
      return;
    }

    const {cancelTokenSource} = this.state;
    this.setState({groupsLoaded: false});

    const onFinally = () => {
      this.setState({
        groupsLoaded: true
      })
    };

    getAllResourceGroups(values, cancelTokenSource)
        .then(response =>
            this.setState({
              foundGroups: response,
              searchResourceGroups: values
            })
        )
        .catch(noop)
        .finally(onFinally);
  }, 300);

  addResourceGroupsSegment = (): JSX.Element => {
    return (
        <FinalForm onSubmit={() => {
        }}
                   initialValues={{
                     includeInactive: false
                   }}
                   subscription={{pristine: true}}
                   render={this.renderForm}
        />
    )
  }

  renderForm = ({handleSubmit}: FormRenderProps): React.ReactNode => {
    const {getMessage} = this.props;
    const {foundGroups} = this.state;

    const numResults = foundGroups.length;

    return (
        <form onSubmit={handleSubmit}>
          <PxGrid>
            <Grid.Row>
              <Grid.Column width={3} verticalAlign={"middle"}>
                <Field
                    name="includesInactive"
                    component={CheckBox}
                    label={getMessage("includingInactive")}
                    toggle={true}
                />
              </Grid.Column>
              <Grid.Column width={13}>
                <AddButton buttonMessage={getMessage("button.add")} onClick={() => this.showModalResource()}/>
              </Grid.Column>
            </Grid.Row>
            <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
            <FormSpy subscription={{values: true}} onChange={this.handleChange}/>
          </PxGrid>
        </form>
    );
  };

  renderResourceGroupsSegment = (): JSX.Element => {
    const {getMessage} = this.props;
    const {foundGroups} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundGroups.length}
            rowGetter={this.groupsRowGetter}
            rowRenderer={this.foundGroupsRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 100,
                label: "#",
                flexShrink: 0,
                dataKey: "index"
              },
              {
                width: 500,
                label: (getMessage("resourceGroups.description")),
                dataKey: "description"
              },
              {
                width: 500,
                label: (getMessage("resourceGroups.useInPatientCentral")),
                dataKey: "useInPatientCentral",
                cellRenderer: this.useInCellRenderer
              },
              {
                width: 500,
                label: (getMessage("status")),
                dataKey: "active",
                cellRenderer: this.statusCellRenderer
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}

        />
    )
  }

  renderUpsertResourceGroupsModal = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {addOrEditModalOpen, resourceGroup, resourceGroupId} = this.state;

    return (
        <Modal
            open={addOrEditModalOpen}
            onClose={() => this.hideModal()}
            closeOnDimmerClick={false}
        >

          <Modal.Header>{getMessage("resourceGroups.title")}</Modal.Header>

          <FormModalContentContainer>
            <UpsertResourceGroupForm
                resourceGroup={resourceGroup}
                resourceGroupId={resourceGroupId}
                onSave={this.onResourceGroupsChange}
                onCancel={this.hideModal}
            >
            </UpsertResourceGroupForm>
          </FormModalContentContainer>
        </Modal>
    )
  }

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage("msgEdit")}
              onConfirm={() => this.openAddOrEditModal(cellData)}>
          </EditActionButton>

          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              popupMessage={getMessage("resourceGroups.confirmDelete")}
              onConfirm={() => this.deleteResourceGroup(cellData)}>
          </DeleteActionButton>
        </React.Fragment>
    )
  }

  statusCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    if (cellData) {
      return getMessage("resourceGroups.active")
    }
    return getMessage("resourceGroups.inactive");
  };

  useInCellRenderer = ({cellData}: any) => {
    if (cellData) {
      return <Icon name="check" size="small"/>
    }
    return ""
  }

  groupsRowGetter = ({index}: any) => {
    const {foundGroups} = this.state

    Object.assign(foundGroups[index], {index: index + 1});

    return foundGroups[index]
  };

  foundGroupsRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {foundGroups} = this.state;
    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddOrEditModal(foundGroups[index].id)}
        >
          {columns}
        </div>
    );
  };

  renderErrorSegment = (): React.ReactNode => {
    const {errorMessage} = this.state;

    return (
        <Grid.Row>
          <Grid.Column>
            <Message negative>
              <p>{errorMessage}</p>
            </Message>
          </Grid.Column>
        </Grid.Row>
    );
  };

}

export default withMessages(ResourceGroupsView);
